// ABOUTME: Two-phase modal for creating an OpenAPI key: form phase, then one-time bearer reveal.
// ABOUTME: Bearer reveal cannot be dismissed except by an explicit confirm button.
import { Box, IconButton, InputBase, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import { tokens } from "../../../theme";
import { yupRequired } from "../../../utils/validation";
import { showToastMessage, showToastMessageRequestError } from "../../../utils/toasts";
import { useCreateOpenapikeyMutation } from "../../../newapi/openapikey/openapikeySlice";
import OpenapikeyRightsCheckboxGrid from "../shared/OpenapikeyRightsCheckboxGrid";

const initialForm = { name: '', rights: [] };

const MAddOpenapikey = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [form, setForm] = useState(initialForm);
    const [fieldErrors, setFieldErrors] = useState({});
    const [bearer, setBearer] = useState(null);
    const [confirmingEmptyRights, setConfirmingEmptyRights] = useState(false);

    const [createKey, { isLoading }] = useCreateOpenapikeyMutation();

    const inBearerPhase = bearer !== null;
    const isStandAlone = window.matchMedia('(display-mode: standalone)').matches;

    const resetAndClose = () => {
        setForm(initialForm);
        setFieldErrors({});
        setBearer(null);
        setConfirmingEmptyRights(false);
        if (props.handleClose) props.handleClose();
    };

    const handleModalClose = () => {
        if (inBearerPhase) return;
        resetAndClose();
    };

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setFieldErrors(prev => ({ ...prev, [key]: '' }));
        if (key === 'rights') setConfirmingEmptyRights(false);
    };

    const schema = yup.object().shape({
        name: yupRequired('name', t).max(100, t('name_max_100')),
    });

    const submit = async () => {
        if (isLoading) return;
        try {
            setFieldErrors({});
            await schema.validate(form, { abortEarly: false });

            if (form.rights.length === 0 && !confirmingEmptyRights) {
                setConfirmingEmptyRights(true);
                return;
            }

            const response = await createKey({ name: form.name, rights: form.rights }).unwrap();
            if (response?.data?.bearer) {
                setBearer(response.data.bearer);
            } else {
                resetAndClose();
            }
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, e) => ({ ...acc, [e.path]: e.message }), {});
                setFieldErrors(errors);
            } else {
                showToastMessageRequestError(t("create_failed"), t, err);
            }
        }
    };

    const copyBearer = async () => {
        try {
            await navigator.clipboard.writeText(bearer);
            showToastMessage('success', t('copied'));
        } catch (e) {
            showToastMessageRequestError(t('copy_to_clipboard'), t, e);
        }
    };

    return (
        <SHModal open={props.open} onClose={handleModalClose} showClose={!inBearerPhase}>
            <Box sx={{ textAlign: "center", paddingY: 4, paddingX: 5, display: "flex", flexDirection: "column", gap: 1 }}>
                <Subheading>{inBearerPhase ? t("bearer_secret") : t("new_openapi_key")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: "70vh",
                    overflowY: "auto",
                }}
            >
                {!inBearerPhase && (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 7 }}>
                            <Box sx={{ width: 120 }}><Text light>{t("name")}</Text></Box>
                            <Box sx={{ flex: 1, backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], borderRadius: 4, display: "flex", height: 32 }}>
                                <ShValidatedInput
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    error={fieldErrors.name}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Text light>{t("rights")}</Text>
                            <Box sx={{ marginTop: 1 }}>
                                <OpenapikeyRightsCheckboxGrid
                                    value={form.rights}
                                    onChange={(rights) => handleChange('rights', rights)}
                                />
                            </Box>
                            {confirmingEmptyRights && (
                                <Box sx={{ marginTop: 2, padding: 2, borderRadius: 3, background: colors.red[100], display: "flex", gap: 1, alignItems: "center" }}>
                                    <WarningAmberIcon sx={{ color: colors.red[400] }} />
                                    <Text>{t("no_rights_warning")}</Text>
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {inBearerPhase && (
                    <>
                        <Box sx={{ padding: 2, borderRadius: 3, background: colors.red[100], display: "flex", gap: 1, alignItems: "center" }}>
                            <WarningAmberIcon sx={{ color: colors.red[400] }} />
                            <Text>{t("bearer_warning")}</Text>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], borderRadius: 3, padding: 1 }}>
                            <InputBase
                                value={bearer}
                                readOnly
                                multiline
                                sx={{ flex: 1, fontFamily: "monospace", fontSize: 13, paddingX: 1, wordBreak: "break-all" }}
                            />
                            <IconButton onClick={copyBearer} aria-label={t('copy_to_clipboard')}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>

            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingX: 3,
                    paddingTop: 2,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                }}
            >
                {!inBearerPhase ? (
                    <>
                        <Shbutton isCancel onClick={resetAndClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                            {t("cancel")}
                        </Shbutton>
                        <ButtonGroup version={2}>
                            <Shbutton
                                isLoading={isLoading}
                                className="group-btn"
                                color="green"
                                variant="contained"
                                onClick={submit}
                            >
                                {confirmingEmptyRights ? t("yes") : t("add")}
                            </Shbutton>
                        </ButtonGroup>
                    </>
                ) : (
                    <Box sx={{ marginLeft: "auto" }}>
                        <ButtonGroup version={2}>
                            <Shbutton
                                className="group-btn"
                                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                variant="contained"
                                onClick={resetAndClose}
                            >
                                {t("ive_copied_it")}
                            </Shbutton>
                        </ButtonGroup>
                    </Box>
                )}
            </Box>
        </SHModal>
    );
};

export default MAddOpenapikey;
